// src/components/onboarding/steps/IndustryStep.tsx

import { Box, Card, SimpleGrid, Text, Stack } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import {
  IconShirt,
  IconShoppingCart,
  IconBuildingStore,
  IconPhone,
  IconCoin,
  IconPlane,
  IconCar,
  IconHeartbeat,
  IconCpu,
  IconDeviceTv,
  IconToolsKitchen2,
  IconBuilding,
  IconDots,
} from "@tabler/icons-react";
import { useBranding } from "@/context/BrandingContext";

const INDUSTRIES = [
  { key: "fashion", label: "Fashion", description: "Apparel and fashion brands", icon: IconShirt },
  { key: "retail", label: "Retail", description: "Physical and online retail", icon: IconShoppingCart },
  { key: "ecommerce", label: "E-commerce", description: "Online commerce platforms", icon: IconBuildingStore },
  { key: "telco", label: "Telco", description: "Telecommunications", icon: IconPhone },
  { key: "finance", label: "Finance", description: "Financial services and banking", icon: IconCoin },
  { key: "travel", label: "Travel", description: "Travel and hospitality", icon: IconPlane },
  { key: "automotive", label: "Automotive", description: "Automotive industry", icon: IconCar },
  { key: "healthcare", label: "Healthcare", description: "Healthcare and pharma", icon: IconHeartbeat },
  { key: "technology", label: "Technology", description: "Tech and SaaS companies", icon: IconCpu },
  { key: "media", label: "Media & Entertainment", description: "Media and content", icon: IconDeviceTv },
  { key: "food_beverage", label: "Food & Beverage", description: "Food and drink brands", icon: IconToolsKitchen2 },
  { key: "real_estate", label: "Real Estate", description: "Property and real estate", icon: IconBuilding },
  { key: "other", label: "Other", description: "Other industries", icon: IconDots },
];

type Props = {
  selected: string | null;
  onSelect: (industry: string) => void;
};

export default function IndustryStep({ selected, onSelect }: Props) {
  const { branding } = useBranding();
  return (
    <Stack gap="lg">
      <Box>
        <Text size="xl" fw={700}>
          1 — Choose your industry
        </Text>
        <Text size="sm" c="dimmed" mt={4}>
          This helps us tailor your {branding.productName} experience with relevant templates and insights.
        </Text>
      </Box>

      <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="md">
        {INDUSTRIES.map((industry) => {
          const isSelected = selected === industry.key;
          const Icon = industry.icon;

          return (
            <Card
              key={industry.key}
              padding="md"
              radius="md"
              withBorder
              onClick={() => onSelect(industry.key)}
              style={{
                cursor: "pointer",
                borderColor: isSelected
                  ? "var(--mantine-color-blue-5)"
                  : undefined,
                borderWidth: isSelected ? 2 : 1,
                position: "relative",
                transition: "all 150ms ease",
              }}
              className="onboarding-card"
            >
              {isSelected && (
                <Box
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    backgroundColor: "var(--mantine-color-blue-5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <IconCheck size={14} color="white" />
                </Box>
              )}
              <Stack gap={8} align="center">
                <Icon size={28} stroke={1.5} color="var(--mantine-color-blue-5)" />
                <Text size="sm" fw={600} ta="center">
                  {industry.label}
                </Text>
                <Text size="xs" c="dimmed" ta="center" lineClamp={2}>
                  {industry.description}
                </Text>
              </Stack>
            </Card>
          );
        })}
      </SimpleGrid>
    </Stack>
  );
}
